import { useMemo, useState } from 'react'
import { Alert, Button, Chip, SearchField, Tabs } from '@heroui/react'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { SUPERVISE } from '../lib/putBack'
import type { CounterView, WorkProfile } from '../lib/types'
import { CounterDialog } from './counterDialogs'

/** The profile a desk opens with: the one chosen last time, else the only one. */
export function defaultProfile(profiles: WorkProfile[], remembered: string): string {
  if (remembered && profiles.some((p) => p.id === remembered)) return remembered
  return profiles.length === 1 ? (profiles[0]?.id ?? '') : ''
}

/** The profile this person's open desk runs now (after any Change), to offer on reopening. */
export function runningProfileOf(desk: Pick<CounterView, 'state' | 'staffId' | 'workProfileId'> | undefined, staffId: string): string | undefined {
  return desk && desk.state !== 'closed' && desk.staffId === staffId ? desk.workProfileId : undefined
}

/** Whoever supervises the floor (ticket.supervise: administrator, branch manager,
 *  supervisor) also sees desks in use; a clerk sees only free ones. */
export function seesAllDesks(permissions: readonly string[]): boolean {
  return permissions.includes(SUPERVISE)
}

/** The step the wizard starts on: the profile when the only free desk is already chosen. */
export function firstStep(counters: readonly Pick<CounterView, 'counterId' | 'state'>[], counterId: string): 1 | 2 {
  const free = counters.filter((c) => c.state === 'closed')
  return free.length === 1 && free[0]!.counterId === counterId ? 2 : 1
}

/** The profile a desk opens on straight from step 1: the branch's only one. */
export function onlyProfile(profiles: readonly Pick<WorkProfile, 'id'>[]): string {
  return profiles.length === 1 ? profiles[0]!.id : ''
}

/** The desks step 1 lists: free ones, and every desk when all is asked for and allowed. */
export function desksShown(counters: readonly CounterView[], seeAll: boolean, showAll: boolean): CounterView[] {
  return seeAll && showAll ? [...counters] : counters.filter((c) => c.state === 'closed')
}

/** Choosing a desk's calling profile. */
export function ProfilePicker({
  profiles,
  value,
  onChange,
  letters,
  names,
}: {
  profiles: WorkProfile[]
  value: string
  onChange: (workProfileId: string) => void
  letters: (p: WorkProfile) => string[]
  names: (p: WorkProfile) => string
}) {
  const { say } = useStaffText()
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()
  const rows = useMemo(() => profiles.filter((p) => p.name.toLowerCase().includes(q)), [profiles, q])

  return (
    <div className="cw-picker">
      <SearchField aria-label={say('counter.wizard.search')} value={query} onChange={setQuery} fullWidth>
        <SearchField.Group>
          <SearchField.SearchIcon />
          <SearchField.Input placeholder={say('counter.wizard.search')} />
          <SearchField.ClearButton />
        </SearchField.Group>
      </SearchField>
      <div className="cw-picker__list">
        <div className="cw-options" role="radiogroup" aria-label={say('counter.callingProfile')}>
          {rows.map((p) => (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={p.id === value}
              className="cw-option"
              onClick={() => onChange(p.id)}
            >
              <span className="cw-option__mono" aria-hidden="true">{letters(p).slice(0, 3).join('')}</span>
              <span className="cw-option__text">
                <b>{p.name}</b>
                <small>{names(p)}</small>
              </span>
              <span className="cw-option__radio" aria-hidden="true" />
            </button>
          ))}
          {rows.length === 0 && <p className="cw-empty">{q ? say('counter.nothingMatches') : say('counter.wizard.noProfiles')}</p>}
        </div>
      </div>
    </div>
  )
}

/**
 * Opening a desk, in two steps: which desk, then its work profile. A step with only
 * one choice is skipped. For a supervisor, desks somebody is signed in at are shown
 * greyed with their name, under All desks.
 */
export function OpenCounter({
  counters,
  profiles,
  counterId,
  profileId,
  seeAll,
  onDesk,
  letters,
  names,
  error,
  signedOut,
  busy,
  onOpen,
  onCancel,
}: {
  counters: CounterView[]
  /** The calling profiles this person may use here. */
  profiles: WorkProfile[]
  counterId: string
  /** The profile chosen last time, offered first. */
  profileId: string
  /** Offers All desks (seesAllDesks). */
  seeAll: boolean
  onDesk: (id: string) => void
  letters: (p: WorkProfile) => string[]
  names: (p: WorkProfile) => string
  error: string
  signedOut: string
  busy: boolean
  onOpen: (counterId: string, workProfileId: string) => void
  onCancel: () => void
}) {
  const { say } = useStaffText()
  const [step, setStep] = useState<1 | 2>(() => firstStep(counters, counterId))
  const [motion, setMotion] = useState<'' | 'fwd' | 'back'>('')
  const [showAll, setShowAll] = useState(false)
  const [picked, setPicked] = useState('')
  const selected = picked || defaultProfile(profiles, profileId)

  const free = counters.filter((c) => c.state === 'closed')
  const shown = desksShown(counters, seeAll, showAll)
  const chosen = counters.find((c) => c.counterId === counterId)
  const ready = chosen?.state === 'closed' && profiles.some((p) => p.id === selected) && !busy

  const go = (to: 1 | 2) => {
    setMotion(to === 2 ? 'fwd' : 'back')
    setStep(to)
  }

  return (
    <main className="page page--work">
      <CounterDialog
        wide
        title={say('counter.wizard.title')}
        sub={say('counter.wizard.step', { n: step })}
        onClose={onCancel}
        foot={
          <>
            <Button variant="tertiary" className="cw-wizard__back" onPress={() => (step === 2 ? go(1) : onCancel())}>
              {step === 2 ? say('counter.back') : say('common.cancel')}
            </Button>
            {step === 2 && (
              <Button type="submit" form="cw-wizard" isDisabled={!ready} isPending={busy}>
                {say('counter.wizard.open')}
              </Button>
            )}
          </>
        }
      >
        <form
          id="cw-wizard"
          className="cw-wizard"
          data-motion={motion || undefined}
          key={step}
          onSubmit={(e) => {
            e.preventDefault()
            if (step === 2 && ready) onOpen(counterId, selected)
          }}
        >
          {error && (
            <Alert status="danger" role="alert">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>{error}</Alert.Title>
              </Alert.Content>
            </Alert>
          )}
          {signedOut && (
            <Alert status="accent" role="status">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>{signedOut}</Alert.Title>
              </Alert.Content>
            </Alert>
          )}
          {step === 1 ? (
            <>
              <div className="cw-wizard__title">
                <h3>{say('counter.wizard.chooseDesk')}</h3>
                <Chip size="sm" variant="soft" color="success">{say('counter.wizard.nAvailable', { n: free.length })}</Chip>
              </div>
              {seeAll && (
                <Tabs className="cw-wizard__tabs" selectedKey={showAll ? 'all' : 'free'} onSelectionChange={(k) => setShowAll(k === 'all')}>
                  <Tabs.ListContainer>
                    <Tabs.List aria-label={say('counter.wizard.chooseDesk')}>
                      <Tabs.Tab id="free">
                        {say('counter.wizard.available')}
                        <span className="cw-tabcount">{free.length}</span>
                        <Tabs.Indicator />
                      </Tabs.Tab>
                      <Tabs.Tab id="all">
                        {say('counter.wizard.allDesks')}
                        <span className="cw-tabcount">{counters.length}</span>
                        <Tabs.Indicator />
                      </Tabs.Tab>
                    </Tabs.List>
                  </Tabs.ListContainer>
                </Tabs>
              )}
              <div className="cw-desks">
                {shown.map((c) => {
                  const taken = c.state !== 'closed'
                  return (
                    <button
                      key={c.counterId}
                      type="button"
                      className="cw-desk"
                      data-taken={taken || undefined}
                      aria-current={c.counterId === counterId && !taken ? 'true' : undefined}
                      disabled={taken}
                      title={taken ? say('counter.cannotTakeOver') : undefined}
                      onClick={() => {
                        onDesk(c.counterId)
                        const only = onlyProfile(profiles)
                        if (only && !busy) onOpen(c.counterId, only)
                        else go(2)
                      }}
                    >
                      <span className="cw-desk__icon" aria-hidden="true">
                        <Icon name="counter" size={22} />
                      </span>
                      <span className="cw-desk__name">{c.name}</span>
                      <span className="cw-desk__code">{c.code}</span>
                      <span className="cw-desk__state">{taken ? say('counter.wizard.inUse') : say('counter.wizard.available')}</span>
                      {taken && <span className="cw-desk__who">{c.staffName ?? ''}</span>}
                    </button>
                  )
                })}
                {shown.length === 0 && <p className="cw-empty">{say('counter.wizard.noDesks')}</p>}
              </div>
            </>
          ) : (
            <>
              <div className="cw-wizard__title">
                <h3>{say('counter.wizard.chooseProfile')}</h3>
                <button type="button" className="cw-chosen" onClick={() => go(1)}>
                  <span>{say('counter.desk')}</span>
                  <strong>{chosen?.name ?? ''}</strong>
                  <em>{say('counter.wizard.change')}</em>
                </button>
              </div>
              <ProfilePicker profiles={profiles} value={selected} onChange={setPicked} letters={letters} names={names} />
            </>
          )}
        </form>
      </CounterDialog>
    </main>
  )
}
